// Single source of truth för repetitions-slots.
//
// Ett delavsnitt repeteras TVÅ gånger. Vill du justera när? Ändra `golv` i
// REP_SLOTS nedan — det är enda stället urvalets tidsregler bor. Alla
// UI-komponenter och algoritmen läser härifrån.
//
// HÅRD REGEL: allt som SKAPAR rep-rader itererar `REP_SLOTS`. Allt som LÄSER
// historik måste gå via `hittaSlot`/`ALLA_SLOTS`, som även täcker pensionerade
// intervall — annars tappar en lektion som skapades före en pensionering sin
// stämpling när den arkiveras.
//
// Vill du lägga till en tredje slot igen? Flytta tillbaka "2v" från
// REP_SLOTS_PENSIONERADE till REP_SLOTS och sätt dess `rank`. Uppgifterna
// finns kvar i banken; inget innehåll raderades vid pensioneringen.

export type RepIntervall = "1v" | "2v" | "2m";

/**
 * När ett delavsnitt tidigast får repeteras i en slot. Ett GOLV, inget mål:
 * når inget delavsnitt golvet blir sloten tom — urvalet tar aldrig ett yngre
 * delavsnitt i stället (Erik 2026-09-30: i början av kursen ska repetitionen
 * inte väljas för tidigt).
 *
 *   "fore-forra-lektionen" — delavsnittet blev klart innan klassens senast
 *     arkiverade lektion skapades. Det som gjordes förra lektionspasset
 *     väntar alltså ett pass. Ingen arkiverad lektion ⇒ ingen kandidat.
 *   "alder" — minst `minDagar` kalenderdagar (svensk tid) sedan delavsnittet
 *     blev klart, OCH minst `minDagarSedanForegaende` sedan föregående aktiva
 *     slot gjordes, så två tillfällen aldrig hamnar tätt ihop.
 */
export type RepGolv =
  | { typ: "fore-forra-lektionen" }
  | { typ: "alder"; minDagar: number; minDagarSedanForegaende: number };

export interface RepSlot {
  /** Stabil identitet — sparas i DB och URL:er. Ändra inte i efterhand. */
  intervall: RepIntervall;
  /**
   * ENBART sorteringsordning, lägst först — i den ordning tillfällena infaller
   * i tiden. Är INTE "N av 2": den ordinalen räknas av `repTillfalleEtikett`
   * ur `REP_SLOTS`, så en pensionerad slot inte förskjuter de aktiva.
   */
  rank: number;
  /** Tidigaste tillfälle för repetition — se RepGolv. */
  golv: RepGolv;
  /** Prisma-kolumn där tidsstämpeln för utförd rep sparas. */
  repField: "rep1At" | "rep2At" | "rep3At";
}

/** De slots som delas ut till NYA lektioner. */
export const REP_SLOTS: readonly RepSlot[] = [
  {
    intervall: "1v",
    rank: 0,
    golv: { typ: "fore-forra-lektionen" },
    repField: "rep1At",
  },
  {
    intervall: "2m",
    rank: 2,
    golv: { typ: "alder", minDagar: 30, minDagarSedanForegaende: 14 },
    repField: "rep3At",
  },
] as const;

// Pensionerade slots: delas aldrig ut till nya lektioner, men finns kvar i
// historiken (LektionDelavsnitt, RepetitionGjord, KlassDelavsnittStatus.rep2At)
// och i uppgiftsbanken. Läsvägar måste känna dem — se hittaSlot.
//
// "2v" (ca 2 veckor) pensionerades 2026-08-14 när repetitionen gick från tre
// tillfällen till två. Ingenting raderades: de 628 originaluppgifterna ligger
// kvar, och en elev som redan börjat en 2v-repetition kan göra klart den.
export const REP_SLOTS_PENSIONERADE: readonly RepSlot[] = [
  {
    intervall: "2v",
    // Mellan 1v och 2m — en gammal rad sorterar där den hörde hemma i tiden.
    rank: 1,
    // Används aldrig för urval (sloten delar inte ut) — finns för typens skull.
    golv: { typ: "alder", minDagar: 14, minDagarSedanForegaende: 0 },
    repField: "rep2At",
  },
] as const;

/** Aktiva + pensionerade, i rank-ordning. Använd för UPPSLAG, aldrig för utdelning. */
export const ALLA_SLOTS: readonly RepSlot[] = [
  ...REP_SLOTS,
  ...REP_SLOTS_PENSIONERADE,
].sort((a, b) => a.rank - b.rank);

/**
 * Slot-definitionen för ett intervall som kommer ur DB eller en URL — alltså
 * möjligen ett pensionerat. Returnerar undefined bara för värden som aldrig
 * funnits.
 */
export function hittaSlot(intervall: string): RepSlot | undefined {
  return ALLA_SLOTS.find((s) => s.intervall === intervall);
}

/** Antal tillfällen ett delavsnitt repeteras — nämnaren i "N av 2". */
export const REP_ANTAL_TILLFALLEN = REP_SLOTS.length;

/** Intervallen som delas ut, i tidsordning. */
export const REP_INTERVALL_ORDER: RepIntervall[] = REP_SLOTS.map((s) => s.intervall);

/** Sortera rader med ett intervall i tidsordning (1v → 2v → 2m). */
export function sorteraRepEfterSlot<T extends { intervall: RepIntervall }>(
  rader: T[],
): T[] {
  return [...rader].sort(
    (a, b) => REP_INTERVALL_RANK[a.intervall] - REP_INTERVALL_RANK[b.intervall],
  );
}

// Indexeras med intervall ur DB-rader → måste täcka ALLA_SLOTS, inte bara de
// aktiva. En gammal 2v-rad ska sortera mellan 1v och 2m, inte krascha på
// undefined.
export const REP_INTERVALL_RANK: Record<RepIntervall, number> = Object.fromEntries(
  ALLA_SLOTS.map((s) => [s.intervall, s.rank]),
) as Record<RepIntervall, number>;

/**
 * Etiketten för vilket tillfälle i ordningen ett intervall är — "1 av 2".
 *
 * AKTIVA slots numreras mot varandra: 1v → "1 av 2", 2m → "2 av 2". Den
 * pensionerade sloten får INTE räknas med, annars förskjuts de aktiva: med
 * 2v inräknad blev 2m "3 av 3" trots att det är det andra av två tillfällen.
 *
 * En PENSIONERAD slot får inget nummer alls utan visas som "extra". Att ge den
 * ett (t.ex. "2 av 3") gjorde att samma rad hette en sak för eleven och en
 * annan för läraren, och att två rader kunde heta "2 av …" bredvid varandra.
 */
export function repTillfalleEtikett(intervall: RepIntervall): string {
  const aktiv = REP_SLOTS.findIndex((s) => s.intervall === intervall);
  if (aktiv >= 0) return `${aktiv + 1} av ${REP_ANTAL_TILLFALLEN}`;
  return "extra";
}

/**
 * Är intervallet en slot som fortfarande DELAS UT?
 *
 * Bor här, inte i den route som råkade behöva den först: "vilka slots är
 * aktiva" är slot-modulens fråga. Skrivvägar MÅSTE använda den här — inte
 * `isRepIntervall`, som medvetet släpper igenom pensionerade intervall för att
 * historiska rader och URL:er ska gå att läsa. En POST som grindas med
 * `isRepIntervall` kan skapa en helt ny rad i en pensionerad slot.
 */
export function arAktivSlot(v: unknown): v is RepIntervall {
  return typeof v === "string" && REP_SLOTS.some((s) => s.intervall === v);
}

/**
 * Är intervallet ett giltigt värde över huvud taget — aktivt ELLER pensionerat?
 * För LÄSVÄGAR. Se `arAktivSlot` för skrivvägar.
 */
export function isRepIntervall(v: unknown): v is RepIntervall {
  return typeof v === "string" && ALLA_SLOTS.some((s) => s.intervall === v);
}
